import { useEffect, useMemo, useRef } from 'react';
import { AdaptiveCard } from 'adaptivecards-react';
import { useTheme } from '@mui/material';
import styles from './ccf-process-automation.module.scss';

export interface CcfProcessAutomationCardProps {
  card: Record<string, any>;
  onSubmit: (values: Record<string, any>) => void;
}

/**
 * Renders a task form Adaptive Card styled like the Task Assist design:
 * labels with a red required marker, outlined fields and right aligned
 * Back / Run buttons under a divider.
 * @param props - CcfProcessAutomationCardProps
 * @example <CcfProcessAutomationCard />
 * @returns Adaptive Card
 */
export function CcfProcessAutomationCard({
  card,
  onSubmit,
}: CcfProcessAutomationCardProps) {
  const theme = useTheme();
  const rootRef = useRef<HTMLDivElement | null>(null);

  const hostConfig = useMemo(
    () => ({
      fontFamily: theme.typography.fontFamily,
      supportsInteractivity: true,
      spacing: {
        small: 4,
        default: 12,
        medium: 16,
        large: 20,
        extraLarge: 24,
        padding: 0,
      },
      fontSizes: {
        small: 12,
        default: 13,
        medium: 14,
        large: 16,
        extraLarge: 18,
      },
      separator: {
        lineThickness: 1,
        lineColor: theme?.palette?.divider,
      },
      inputs: {
        label: {
          inputSpacing: 'small',
          requiredInputs: {
            color: 'default',
            size: 'default',
            weight: 'default',
            suffix: ' *',
            suffixColor: 'attention',
          },
          optionalInputs: {
            color: 'default',
            size: 'default',
            weight: 'default',
          },
        },
      },
      actions: {
        actionsOrientation: 'horizontal',
        actionAlignment: 'right',
        buttonSpacing: 8,
        maxActions: 5,
        spacing: 'default',
      },
      containerStyles: {
        default: {
          backgroundColor: 'transparent',
          foregroundColors: {
            default: {
              default: theme?.palette?.text?.primary,
              subtle: theme?.palette?.text?.secondary,
            },
            attention: {
              default: theme?.palette?.error?.main,
              subtle: theme?.palette?.error?.main,
            },
          },
        },
      },
    }),
    [theme]
  );

  // The panel rebuilds the card object on every render; keying on its content
  // keeps the rendered card (and what the agent typed) until it really changes
  const cardContent = JSON.stringify(card);
  const payload = useMemo(() => JSON.parse(cardContent), [cardContent]);

  // Labels ending with " *" show the red required marker of the design without
  // turning on Adaptive Card input validation (isRequired blocks submit)
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    /**
     * Wraps the trailing asterisk of input labels in a styled span
     * @example markRequiredLabels()
     */
    const markRequiredLabels = () => {
      root.querySelectorAll('label').forEach((label) => {
        const textNode = Array.from(label.childNodes)
          .reverse()
          .find((node) => node.nodeType === Node.TEXT_NODE && node.textContent);
        const text = textNode?.textContent ?? '';
        if (!textNode || !/\s\*\s*$/.test(text)) return;
        textNode.textContent = text.replace(/\s\*\s*$/, ' ');
        const marker = document.createElement('span');
        marker.className = styles.requiredMarker;
        marker.textContent = '*';
        label.appendChild(marker);
      });
    };
    markRequiredLabels();
    const observer = new MutationObserver(markRequiredLabels);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div className={styles.formCard} ref={rootRef}>
      <AdaptiveCard
        style={{ fontFamily: theme.typography.fontFamily }}
        payload={payload}
        hostConfig={hostConfig}
        onExecuteAction={(action: any) => onSubmit({ ...(action?.data ?? {}) })}
      />
    </div>
  );
}

export default CcfProcessAutomationCard;
